import {useTheme} from '@mui/material/styles'
import PropTypes from 'prop-types'
import React, {useState} from 'react'
import useMediaQuery from '@mui/material/useMediaQuery'

import {KEYRUS_AI_NAV_COMPACT_QUERY} from 'helpers/keyrusAi'

const TOP_NAV = [
  {label: 'The New Category', href: '#hero'},
  {label: 'Meet Keyrus', href: '#meet-keyrus'},
  {label: 'HOM™', href: '#hom'},
  {label: 'The Intelligence Era', href: '#intelligence-eras'},
  {label: 'Contact', href: '#contact'},
]

// Lets sections (e.g. KeyrusAiEras) replay their entrance animation on click,
// including same-hash clicks that wouldn't otherwise fire a hashchange.
const NAV_CLICK_EVENT = 'keyrus-ai-nav'

const emitNavClick = href => {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent(NAV_CLICK_EVENT, {detail: {href}}))
  }
}

const HamburgerIcon = () => (
  <svg
    aria-hidden="true"
    fill="none"
    height="11"
    viewBox="0 0 16 11"
    width="16"
    xmlns="http://www.w3.org/2000/svg"
  >
    <rect fill="#ffffff" height="1.5" rx="0.75" width="16" y="0" />
    <rect fill="#ffffff" height="1.5" rx="0.75" width="16" y="4.75" />
    <rect fill="#ffffff" height="1.5" rx="0.75" width="16" y="9.5" />
  </svg>
)

const CloseIcon = () => (
  <svg
    aria-hidden="true"
    fill="none"
    height="12"
    viewBox="0 0 12 12"
    width="12"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M1 1L11 11M11 1L1 11"
      stroke="white"
      strokeLinecap="round"
      strokeWidth="1.5"
    />
  </svg>
)

const getStyles = theme => {
  const navZIndex = (theme.customZindex?.header || 1000000) + 10

  return {
    nav: {
      position: 'fixed',
      top: 18,
      left: '50%',
      transform: 'translateX(-50%)',
      zIndex: navZIndex,
      display: 'flex',
      flexWrap: 'wrap',
      gap: 2,
      alignItems: 'center',
      justifyContent: 'center',
      boxSizing: 'border-box',
      width: 'max-content',
      maxWidth: 'min(860px, calc(100vw - 300px))',
      padding: '6px 8px',
      borderRadius: 999,
      background: 'rgba(7, 16, 31, 0.55)',
      border: '1px solid rgba(255, 255, 255, 0.18)',
      boxShadow: '0 12px 34px rgba(0, 0, 0, 0.28)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
    },
    navItem: {
      display: 'inline-flex',
      alignItems: 'center',
      height: 36,
      padding: '0 18px',
      borderRadius: 999,
      color: '#ffffff',
      fontSize: 14,
      fontWeight: 500,
      letterSpacing: 0.2,
      textDecoration: 'none',
      whiteSpace: 'nowrap',
    },
    hamburger: {
      position: 'fixed',
      top: 25,
      right: 18,
      width: 31,
      height: 31,
      borderRadius: 6.2,
      background: 'rgba(7, 16, 31, 0.55)',
      border: '1px solid rgba(255, 255, 255, 0.18)',
      boxShadow: '0 8px 22px rgba(0, 0, 0, 0.28)',
      backdropFilter: 'blur(12px)',
      WebkitBackdropFilter: 'blur(12px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      zIndex: navZIndex,
      padding: 0,
    },
    overlay: {
      position: 'fixed',
      inset: 0,
      background: 'rgba(0, 0, 0, 0.79)',
      backdropFilter: 'blur(6.75px)',
      WebkitBackdropFilter: 'blur(6.75px)',
      zIndex: navZIndex,
      fontFamily: "'Montserrat', sans-serif",
    },
    closeBtn: {
      position: 'absolute',
      top: 25,
      right: 18,
      width: 31,
      height: 31,
      borderRadius: 6.2,
      background: 'rgba(217, 217, 217, 0.31)',
      border: 'none',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      cursor: 'pointer',
      padding: 0,
    },
    overlayNav: {
      position: 'absolute',
      top: 80,
      left: 0,
      right: 0,
      display: 'flex',
      flexDirection: 'column',
    },
    overlayItem: {
      display: 'flex',
      alignItems: 'center',
      height: 56,
      padding: '10px 25px',
      borderBottom: '1px solid rgba(255, 255, 255, 0.2)',
      color: '#ffffff',
      fontSize: 22,
      fontWeight: 300,
      textDecoration: 'none',
      boxSizing: 'border-box',
    },
  }
}

const KeyrusAiNav = ({copy}) => {
  const theme = useTheme()
  const styles = getStyles(theme)
  const isCompact = useMediaQuery(KEYRUS_AI_NAV_COMPACT_QUERY)
  const [isOpen, setIsOpen] = useState(false)
  const navItems = copy && copy.length ? copy : TOP_NAV

  const open = () => setIsOpen(true)
  const close = () => setIsOpen(false)

  if (isCompact) {
    return (
      <>
        <button
          aria-label="Open navigation menu"
          onClick={open}
          style={styles.hamburger}
          type="button"
        >
          <HamburgerIcon />
        </button>

        {isOpen && (
          <div
            aria-label="Navigation menu"
            role="dialog"
            style={styles.overlay}
          >
            <button
              aria-label="Close navigation menu"
              onClick={close}
              style={styles.closeBtn}
              type="button"
            >
              <CloseIcon />
            </button>
            <nav
              aria-label="Keyrus AI landing sections"
              style={styles.overlayNav}
            >
              {navItems.map(item => (
                <a
                  key={item.label}
                  href={item.href}
                  onClick={() => {
                    emitNavClick(item.href)
                    close()
                  }}
                  style={styles.overlayItem}
                >
                  {item.label}
                </a>
              ))}
            </nav>
          </div>
        )}
      </>
    )
  }

  return (
    <nav aria-label="Keyrus AI landing sections" style={styles.nav}>
      {navItems.map(item => (
        <a
          key={item.label}
          href={item.href}
          onClick={() => emitNavClick(item.href)}
          style={styles.navItem}
        >
          {item.label}
        </a>
      ))}
    </nav>
  )
}

KeyrusAiNav.propTypes = {
  copy: PropTypes.arrayOf(
    PropTypes.shape({
      href: PropTypes.string,
      label: PropTypes.string,
    }),
  ),
}

KeyrusAiNav.defaultProps = {
  copy: null,
}

export default KeyrusAiNav
